import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import React, { useEffect, useState } from 'react';
import { Control, UseFormSetValue, useFormContext } from 'react-hook-form';
import { User } from '../../../../../types/user';
import { usesNirovision } from '../../../../Experiences/utils';
import { reservationEditFieldLabelSx } from '../../../EditReservation/reservationEditFieldStyles';
import AdditionalServices from './AdditionalServices';
import ExternalAttendees from './ExternalAttendees';
import { RsvpConfig } from './RsvpConfig';
import StaffAttendees, { AutocompleteStaffOption } from './StaffAttendees';

type Props = {
    setValue: UseFormSetValue<any>;
    experienceDetails: any;
    guestCount: number;
    showStaffBlocks: boolean;
    showAdditionalServices?: boolean;
    control?: Control<any>;
    reservationDateOptions?: string[];
    isCatering?: boolean;
    isMassage?: boolean;
    isAlcohol?: boolean;
    hasCateringContractors?: boolean;
    hasMassageContractors?: boolean;
    hasAlocholSetMenus?: boolean;
    hasCateringSetMenus?: boolean;
    cateringDetails?: string;
    beverageDetails?: string;
    massageDetails?: string;
};

export const pickedEmails = (array: Array<User | { email: string }>) =>
    array.map((value) => (value?.email || '').toLowerCase()).filter(Boolean);

export const dedupeByEmail = (items: Array<User | { email: string }>) => {
    const map = new Map<string, User | { email: string }>();
    items.forEach((i) => {
        const em = String((i as any)?.email || '').toLowerCase();
        if (em) map.set(em, i);
    });
    return Array.from(map.values());
};

export const defaultAutocompleteFilter =
    createFilterOptions<AutocompleteStaffOption>();

export const getAutocompleteOptionEmail = (option: AutocompleteStaffOption) => {
    if ('value' in option) return option.value?.email;
    return option.inputValue?.toLowerCase();
};

export const toSelected = (current: Array<User | { email: string }>) =>
    current.map((item) => {
        const email = (item as any)?.email || '';
        const first = (item as any)?.firstName;
        const last = (item as any)?.lastName;

        if (first || last) {
            return {
                value: item as User,
                label: `${first ?? ''} ${last ?? ''} (${email})`.trim(),
            };
        }

        return {
            inputValue: email,
            label: email,
        };
    });

export const isValidEmail = (email: string): boolean => {
    if (!email) return false;
    const trimmed = email.trim();
    const basicPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!basicPattern.test(trimmed)) return false;
    const [local, domain] = trimmed.split('@');
    if (!local || !domain) return false;
    if (local.startsWith('.') || local.endsWith('.') || local.includes('..'))
        return false;
    if (
        domain.startsWith('.') ||
        domain.endsWith('.') ||
        domain.startsWith('-') ||
        domain.endsWith('-') ||
        domain.includes('..') ||
        domain.includes(',')
    )
        return false;
    const labels = domain.split('.');
    if (
        labels.some(
            (label) =>
                label.length === 0 ||
                !/^[A-Za-z0-9-]+$/.test(label) ||
                label.startsWith('-') ||
                label.endsWith('-'),
        )
    )
        return false;
    return true;
};

export default function AttendeesSection({
    setValue,
    experienceDetails,
    guestCount,
    showStaffBlocks,
    showAdditionalServices = false,
    control,
    reservationDateOptions = [],
    isCatering = false,
    isMassage = false,
    isAlcohol = false,
    hasCateringContractors = false,
    hasMassageContractors = false,
    hasAlocholSetMenus = false,
    hasCateringSetMenus = false,
    cateringDetails,
    beverageDetails,
    massageDetails,
}: Props) {
    const usesFaceRec = usesNirovision(experienceDetails);
    const {
        formState: { errors },
    } = useFormContext();
    const [attendeesExpanded, setAttendeesExpanded] = useState(false);

    useEffect(() => {
        const hasAttendeeError = Boolean(
            (errors as any)?.staffAttending ||
            (errors as any)?.externalAttendees ||
            (errors as any)?.rsvpConfig,
        );
        if (hasAttendeeError) {
            setAttendeesExpanded(true);
        }
    }, [errors]);

    const attendeesContent = (
        <Stack spacing={2.5}>
            <StaffAttendees
                experienceType={experienceDetails?.type}
                usesFaceRec={usesFaceRec}
                isEditing={false}
            />
            <ExternalAttendees
                experienceType={experienceDetails?.type}
                guestCount={guestCount}
                usesFaceRec={usesFaceRec}
                isEditing={false}
            />
            <RsvpConfig embedded />
        </Stack>
    );

    return (
        <>
            {showStaffBlocks && (
                <>
                    <Accordion
                        expanded={attendeesExpanded}
                        onChange={(_, next) => setAttendeesExpanded(next)}
                        disableGutters
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'custom.lightGrey',
                            backgroundColor: 'background.paper',
                            overflow: 'hidden',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{ color: 'text.secondary' }}
                                />
                            }
                            sx={{
                                px: 2,
                                py: 1.25,
                                minHeight: 56,
                                '& .MuiAccordionSummary-content': {
                                    my: 0.5,
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                },
                            }}
                        >
                            <Typography
                                variant='body3'
                                sx={reservationEditFieldLabelSx}
                            >
                                Attendees
                            </Typography>
                            <Typography
                                variant='body3'
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.35,
                                    fontWeight: 400,
                                }}
                            >
                                Staff, guests, and RSVP settings
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                            {attendeesContent}
                        </AccordionDetails>
                    </Accordion>
                    {showAdditionalServices && control && (
                        <Box sx={{ mt: 2 }}>
                            <AdditionalServices
                                experienceDetails={experienceDetails}
                                control={control}
                                reservationDateOptions={reservationDateOptions}
                                isCatering={isCatering}
                                isMassage={isMassage}
                                isAlcohol={isAlcohol}
                                cateringDetails={cateringDetails}
                                beverageDetails={beverageDetails}
                                massageDetails={massageDetails}
                            />
                        </Box>
                    )}
                </>
            )}
        </>
    );
}
